import { ApolloClient, ApolloLink, InMemoryCache } from '@apollo/client/core';
import { setContext } from '@apollo/client/link/context';
import { onError } from '@apollo/client/link/error';
import createUploadLink from 'apollo-upload-client/createUploadLink.mjs';
import { print } from 'graphql';

import { generateXSignature } from './utils/signature';

const uploadLink = createUploadLink({
  uri: `${window.location.origin}/graphql`,
});

const errorLink = onError(({ graphQLErrors, networkError }) => {
  if (graphQLErrors) {
    for (const err of graphQLErrors) {
      if (
        err.extensions?.code === 'UNAUTHENTICATED' ||
        err.message === 'Unauthorized'
      ) {
        sessionStorage.clear();
        window.location.href = '/login';
      }
    }
  }
  if (
    networkError &&
    'statusCode' in networkError &&
    (networkError.statusCode === 401 || networkError.statusCode === 426)
  ) {
    sessionStorage.clear();
    window.location.href = '/login';
  }
});

const authSigLink = setContext(async (operation, prevContext) => {
  const hmacSecret = sessionStorage.getItem('hmacSecret') || '';
  if (!hmacSecret || operation.operationName === 'login') {
    return {
      headers: {
        ...(prevContext?.headers ?? {}),
      },
    };
  }

  const timestamp = Date.now().toString();
  const bodyForSign = print(operation.query);
  const xSig = await generateXSignature(timestamp, bodyForSign);

  return {
    headers: {
      ...(prevContext?.headers ?? {}),
      authorization: sessionStorage.getItem('authToken') || '',
      'x-timestamp': timestamp,
      'x-signature': xSig,
    },
  };
});

const headerLink = new ApolloLink((operation, forward) => {
  return forward(operation).map((response) => {
    const headers = operation.getContext().response?.headers;
    if (headers) {
      const token = headers.get('new-authorization');
      if (token) sessionStorage.setItem('authToken', token);

      const tokenhmac = headers.get('new-hmac-secret');
      if (tokenhmac) sessionStorage.setItem('hmacSecret', tokenhmac);
    }
    return response;
  });
});

// Cache implementation
const cache = new InMemoryCache({
  typePolicies: {
    Query: {
      fields: {
        listItemCategories: {
          merge(existing = {}, incoming) {
            return {
              ...existing,
              ...incoming,
              itemCategories:
                incoming.itemCategories || existing.itemCategories,
              uncategorizedItems:
                incoming.uncategorizedItems || existing.uncategorizedItems,
              pageInfo: incoming.pageInfo || existing.pageInfo,
            };
          },
        },
      },
    },
    keypad: {
      keyFields: ['workspaceID', 'shopID', 'keypadID'],
    },
    shop: {
      keyFields: ['brandID', 'shopID'],
    },
  },
});

export const apolloClient = new ApolloClient({
  link: ApolloLink.from([errorLink, authSigLink, headerLink, uploadLink]),
  cache,
});
